import type { FC } from "react"
import { useState } from "react"

import { useApprovePayFile } from "../hooks/useApprovePayFile"
import { useRemovePayFileApproval } from "../hooks/useRemovePayFileApproval"

import { PayFileRemoveApprovalConfirmationDialog } from "./PayFileRemoveApprovalConfirmationDialog"

import { Button } from "@/components/common/Button"
import ConfirmationDialog from "@/components/common/Modals/ConfirmationDialog"
import { type PayFileListFieldsFragment, PayFileStatus } from "@/graphql/codegen/graphql"

interface PayFileApprovalActionsProps {
  payFile: PayFileListFieldsFragment
  onSuccess: () => void
}

export const PayFileApprovalActions: FC<PayFileApprovalActionsProps> = ({ payFile, onSuccess }) => {
  const [isApproveConfirmationOpen, setIsApproveConfirmationOpen] = useState(false)
  const [isRemoveApprovalConfirmationOpen, setIsRemoveApprovalConfirmationOpen] = useState(false)

  const { approvePayFile, isApproving } = useApprovePayFile()
  const { removePayFileApproval, isRemovingApproval } = useRemovePayFileApproval()

  // A failed mutation leaves the confirmation open — the hook already notified the error, so the
  // user can retry or go back without re-opening from the row.
  const handleApproveConfirm = async () => {
    const isSuccess = await approvePayFile({ payFileId: payFile.payFileId })
    if (!isSuccess) return

    setIsApproveConfirmationOpen(false)
    onSuccess()
  }

  const handleRemoveApprovalConfirm = async (comment: string) => {
    const isSuccess = await removePayFileApproval({ payFileId: payFile.payFileId, comment })
    if (!isSuccess) return

    setIsRemoveApprovalConfirmationOpen(false)
    onSuccess()
  }

  // AK-5102: Only Pending and Approved pay files carry a CTA; Cancelled and Sent are terminal.
  // The owning cell provides the flex layout, so no wrapper here.
  if (payFile.status === PayFileStatus.Pending) {
    return (
      <>
        <Button
          appearance='outlined'
          rootElementName='button'
          onClick={() => setIsApproveConfirmationOpen(true)}
          data-testid={`pay-file.approval-actions.approve-button-${payFile.payFileId}`}
        >
          Approve
        </Button>
        {isApproveConfirmationOpen && (
          <ConfirmationDialog
            title='Are you sure you want to approve this pay file?'
            content='This action will mark the pay file as approved for processing.'
            closeLabel='Go Back'
            confirmLabel='Approve Pay File'
            confirmDisabled={isApproving}
            testIdPrefix='pay-file.approve-confirmation-dialog'
            onClose={() => setIsApproveConfirmationOpen(false)}
            onConfirm={handleApproveConfirm}
          />
        )}
      </>
    )
  }

  if (payFile.status === PayFileStatus.Approved) {
    return (
      <>
        <Button
          appearance='outlined'
          rootElementName='button'
          onClick={() => setIsRemoveApprovalConfirmationOpen(true)}
          data-testid={`pay-file.approval-actions.remove-approval-button-${payFile.payFileId}`}
        >
          Remove Approval
        </Button>
        {isRemoveApprovalConfirmationOpen && (
          <PayFileRemoveApprovalConfirmationDialog
            isSubmitting={isRemovingApproval}
            onClose={() => setIsRemoveApprovalConfirmationOpen(false)}
            onConfirm={handleRemoveApprovalConfirm}
          />
        )}
      </>
    )
  }

  return null
}
